import { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, XCircle, RotateCcw, Edit3 } from 'lucide-react';
import { supabase } from '../../services/supabaseService';
import type { IngestionJob } from '../../types/proposal-blocks';

interface ProcessingViewProps {
  proposalId: string;
  jobId: string;
  onReview: (proposalId: string) => void;
  onBack: () => void;
}

const STATUS_LABELS: Record<string, { label: string; detail: string }> = {
  queued: { label: 'En cola', detail: 'Preparando procesamiento...' },
  extracting: { label: 'Extrayendo', detail: 'Leyendo documentos y extrayendo contenido...' },
  understanding: { label: 'Analizando', detail: 'La IA está entendiendo la estructura del documento...' },
  structuring: { label: 'Estructurando', detail: 'Generando propuesta con bloques tipados...' },
  ready_for_review: { label: 'Listo', detail: '¡La propuesta está lista para revisión!' },
  failed: { label: 'Error', detail: 'Hubo un problema al procesar los documentos.' },
  published: { label: 'Publicado', detail: 'La propuesta ha sido publicada.' },
};

export function ProcessingView({ proposalId, jobId, onReview, onBack }: ProcessingViewProps) {
  const [job, setJob] = useState<Partial<IngestionJob>>({ status: 'queued', progress: 0 });
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    // Initial fetch
    (async () => {
      const { data } = await supabase
        .from('ingestion_jobs')
        .select('*')
        .eq('id', jobId)
        .single();
      if (data) setJob(data);
    })();

    // Realtime subscription
    const channel = supabase
      .channel(`job-${jobId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'ingestion_jobs', filter: `id=eq.${jobId}` },
        (payload) => {
          setJob(payload.new as IngestionJob);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [jobId]);

  // Auto-redirect when ready
  useEffect(() => {
    if (job.status === 'ready_for_review') {
      const timer = setTimeout(() => onReview(proposalId), 1500);
      return () => clearTimeout(timer);
    }
  }, [job.status, proposalId, onReview]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      // Create new job
      const { data: newJob, error } = await supabase
        .from('ingestion_jobs')
        .insert({ proposal_id: proposalId, status: 'queued', progress: 0 })
        .select()
        .single();

      if (error || !newJob) throw new Error('Error creating retry job');

      setJob({ status: 'queued', progress: 0 });

      // Trigger edge function
      await supabase.functions.invoke('ingest-proposal', {
        body: { proposal_id: proposalId, job_id: newJob.id },
      });
    } catch (e) {
      console.error('Retry failed:', e);
    } finally {
      setRetrying(false);
    }
  };

  const statusInfo = STATUS_LABELS[job.status || 'queued'] || STATUS_LABELS.queued;
  const isComplete = job.status === 'ready_for_review' || job.status === 'published';
  const isFailed = job.status === 'failed';

  return (
    <div className="p-6 max-w-lg mx-auto flex flex-col items-center justify-center min-h-[60vh]">
      {/* Status icon */}
      <div className="mb-6">
        {isComplete ? (
          <CheckCircle2 className="w-16 h-16 text-green-500" />
        ) : isFailed ? (
          <XCircle className="w-16 h-16 text-red-500" />
        ) : (
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
        )}
      </div>

      {/* Status text */}
      <h2 className="text-xl font-bold text-gray-900 mb-2">{statusInfo.label}</h2>
      <p className="text-sm text-gray-500 text-center mb-8">{statusInfo.detail}</p>

      {/* Progress bar */}
      {!isComplete && !isFailed && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${job.progress || 0}%` }}
          />
        </div>
      )}
      {!isComplete && !isFailed && (
        <p className="text-xs text-gray-400 mb-8">{job.progress || 0}%</p>
      )}

      {/* Error message */}
      {isFailed && job.error_message && (
        <div className="w-full bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-700">{job.error_message}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {isFailed && (
          <>
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              <RotateCcw size={14} /> {retrying ? 'Reintentando...' : 'Reintentar'}
            </button>
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              <Edit3 size={14} /> Editar manualmente
            </button>
          </>
        )}

        {isComplete && (
          <button
            onClick={() => onReview(proposalId)}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
          >
            <CheckCircle2 size={14} /> Revisar propuesta
          </button>
        )}
      </div>

      {/* Cost info */}
      {job.llm_cost_usd != null && (
        <p className="text-xs text-gray-400 mt-6">
          Coste IA: ${Number(job.llm_cost_usd).toFixed(4)} · {(job.llm_input_tokens || 0) + (job.llm_output_tokens || 0)} tokens · {job.llm_model}
        </p>
      )}
    </div>
  );
}
